import { createFetchAdapter, createHqApi } from '@hq/api-sdk';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface HqRuntimeConfig {
  /** 主业务 API 根（含 `/api` 段）。 */
  apiBase: string;
  /** 资源服务根（含 `/res` 段）。 */
  resBase: string;
  appTitle: string;
  uploadMaxMb: number;
  enableDebug: boolean;
}

export interface HqProfile {
  userId: string;
  orgId: string;
  /** 机构名称（后端下发；旧会话可能没有）。 */
  orgName?: string | null;
  deptId: string;
  name: string;
  /** 账号等级，如 L3（DR-8.6）。 */
  level: string;
  /** 后端下发的权限码；undefined 表示旧会话/开发态发号，不做前端菜单裁剪。 */
  perms?: string[];
  /** 个人账户：头像（资源服务 fileUrl）、工号、科室、脱敏手机、邮箱。 */
  avatarUrl?: string | null;
  userCode?: string | null;
  deptName?: string | null;
  mobile?: string | null;
  email?: string | null;
}

declare global {
  interface Window {
    /** 部署时由 public/config.js 注入；缺失时回退默认值。 */
    __HQ_CONFIG__?: Partial<HqRuntimeConfig>;
  }
}

const fallback: HqRuntimeConfig = {
  apiBase: '/api',
  resBase: '/res',
  appTitle: '行政物资运营平台',
  uploadMaxMb: 50,
  enableDebug: false
};

/** 本机 dev 强制同源，走 Vite 反代（5005→5001/5002）；生产绝对地址由 config.js 提供。 */
const devOverride: Partial<HqRuntimeConfig> = import.meta.env.DEV ? { apiBase: '/api', resBase: '/res' } : {};

/** 运行时配置：`public/config.js` 覆盖默认值，同一份产物可跨环境部署。 */
const runtime: HqRuntimeConfig = { ...fallback, ...(window.__HQ_CONFIG__ ?? {}), ...devOverride };

export const config: HqRuntimeConfig = runtime;

export const appTitle = config.appTitle;

/** 账号等级中文名收敛在 `@hq/utils`，此处 re-export 保持端内既有 import 不变。 */
export { LEVEL_LABELS, levelLabel } from '@hq/utils';

interface AuthState {
  token: string | null;
  profile: HqProfile | null;
  setSession: (token: string, profile: HqProfile) => void;
  /** 会话局部更新（头像/科室等由 /auth/me 补全）。 */
  patchProfile: (patch: Partial<HqProfile>) => void;
  clear: () => void;
}

/** Web 端令牌存内存 + sessionStorage（docs/05 §6-1），关浏览器即失效。 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      profile: null,
      setSession: (token, profile) => set({ token, profile }),
      patchProfile: (patch) =>
        set((state) => ({ profile: state.profile ? { ...state.profile, ...patch } : null })),
      clear: () => set({ token: null, profile: null })
    }),
    {
      // 各站点登录态互不共享：key 带门户名
      name: 'hq-ops-auth',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
);

const getToken = (): string | null => useAuthStore.getState().token;

/**
 * 三端共用的 SDK 实例：注入 fetch 适配器、令牌来源与 401 处理。
 * 业务页面只依赖 `hq.*` 命名空间，不直接碰 fetch。
 */
export const hq = createHqApi({
  adapter: createFetchAdapter(),
  baseUrl: config.apiBase,
  resBaseUrl: config.resBase,
  resolveToken: getToken,
  onUnauthorized: () => useAuthStore.getState().clear()
});
